import { isUndefinedOrEmpty } from '@afterpay/utils/string'
import { isValidAddressComponent, isValidAddressInput, isValidEmail, isValidName, valueIsNotNullOrUndefined } from './validation'
import { isValidPostcode } from './postcode-validations'
import { CheckoutAddress } from '../model'
import type { CountryCode } from 'libphonenumber-js'
import { AllCountryCode, InputAddressData } from '@afterpaytouch/core'
import type { TFunction } from 'i18next'
import { BLACKLISTED_STATES } from '../model/paymentType'

export interface AddressValidationConfig {
  state: boolean
  postcode: boolean
  suburb: boolean
}

export const requiredAddressFieldsByCountry: Record<CountryCode | string, AddressValidationConfig> = {
  AU: {
    state: true,
    postcode: true,
    suburb: true,
  },
  NZ: {
    state: true,
    postcode: true,
    suburb: true,
  },
  US: {
    state: true,
    postcode: true,
    suburb: true,
  },
  CA: {
    state: true,
    postcode: true,
    suburb: true,
  },
  GB: {
    state: false,
    postcode: true,
    suburb: true,
  },
  IM: {
    state: false,
    postcode: true,
    suburb: true,
  },
  JE: {
    state: false,
    postcode: true,
    suburb: true,
  },
  GG: {
    state: false,
    postcode: true,
    suburb: true,
  },
  DEFAULT: {
    state: false,
    postcode: false,
    suburb: true,
  },
}

export const validateProfileField = (country, key, value, isMobileLogin = false): boolean => {
  const requiredFields = requiredAddressFieldsByCountry[country] ?? requiredAddressFieldsByCountry.DEFAULT

  const validate: Record<string, (input: string) => boolean> = {
    givenNames: (name) => isValidName({ name }),
    surname: (name) => isValidName({ name }),
    otherGivenNames: (name) => isValidName({ name, optional: true }),
    dateOfBirth: (input) => !isUndefinedOrEmpty(input),
    mobile: (input) => !isUndefinedOrEmpty(input),
    address1: (input) => isValidAddressComponent({ input }),
    address2: (input) => isValidAddressComponent({ input, optional: true }),
    area2: (input) => isValidAddressComponent({ input, optional: true }),
    countryCode: (input) => !isUndefinedOrEmpty(input),
    postcode: (input) => isValidPostcode(country, input, !requiredFields.postcode),
    state: (input) => isValidAddressComponent({ input, optional: !requiredFields.state }),
    suburb: (input) => isValidAddressComponent({ input, optional: !requiredFields.suburb }),
    email: (input) => (isMobileLogin ? isValidEmail(input) : true),
    creditCheckAgree: (input) => valueIsNotNullOrUndefined(input),
  }

  return validate[key](value)
}

// Returns which single postcode gap (if any) is the ONLY thing failing GB address
// validation: 'incomplete' (outward-only code like "M24"), 'missing' (blank), or null
// (postcode is fine, or other fields are also invalid). Lets the address validators
// show a postcode-specific prompt instead of the generic "select a valid address".
export const gbPostcodeGap = (address: InputAddressData, country: AllCountryCode): 'missing' | 'incomplete' | null => {
  if (validateProfileField(country, 'postcode', address.postcode)) {
    return null
  }
  // Only the address-component fields — not arbitrary keys. Callers can pass a richer
  // object whose extra keys validateProfileField has no handler for, which would
  // otherwise read as a failure and suppress the prompt.
  const addressFields: Array<keyof InputAddressData> = ['address1', 'address2', 'suburb', 'state', 'area2', 'countryCode']
  const restOfAddressValid = addressFields.every((key) => validateProfileField(country, key, address[key]))
  if (!restOfAddressValid) {
    return null
  }
  return typeof address.postcode === 'undefined' || address.postcode.trim().length === 0 ? 'missing' : 'incomplete'
}

// Maps the single GB postcode/street gap to its specific prompt, so the logic isn't
// copy-pasted across the address validators (AccountKYC, VerifyRetry). Check this before
// the generic validAddressOption, which accepts an outward-only postcode and masks the gap.
export const gbAddressGapError = (address: InputAddressData, country: AllCountryCode, t: TFunction): string | null => {
  if (country !== 'GB') {
    return null
  }
  if (typeof address.address1 !== 'string' || address.address1.trim().length === 0) {
    return t('verifyIdentity:error:missingStreet')
  }
  const gap = gbPostcodeGap(address, country)
  if (gap === 'incomplete') {
    return t('verifyIdentity:error:incompletePostcode')
  }
  if (gap === 'missing') {
    return t('verifyIdentity:error:missingPostcode')
  }
  return null
}

export const isValidAddress = (address: CheckoutAddress | undefined): boolean => {
  const requiredFields = requiredAddressFieldsByCountry[address?.countryCode ?? 'DEFAULT'] ?? requiredAddressFieldsByCountry.DEFAULT

  return (
    isValidAddressInput(address?.address1) &&
    isValidPostcode(address?.countryCode, address?.postcode, !requiredFields.postcode) &&
    isValidAddressInput(address?.state, !requiredFields.state) &&
    isValidAddressInput(address?.suburb, !requiredFields.suburb)
  )
}

export const getAddressOptionalFields = (country): string[] => {
  const requiredFields = requiredAddressFieldsByCountry[country] ?? requiredAddressFieldsByCountry.DEFAULT
  return Object.keys(requiredFields).filter((key) => requiredFields[key] === false)
}

export const isConsumerLendingEligibleState = (address: InputAddressData): boolean => {
  const ineligibleStates: string[] = BLACKLISTED_STATES.PCL[address?.countryCode]
  return !ineligibleStates?.includes(address?.state)
}
